import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { TPageFilter } from './types'
import { useGetAppPage } from './useGetAppPage'

interface FiltersLayoutContextType {
  pageFilters: TPageFilter[]
  pinnedFilters: TPageFilter[]
  unPinnedFilters: TPageFilter[]
  unpinFilter: (selectedF: TPageFilter) => void
  pinFilter: (selectedF: TPageFilter, side: 'left' | 'right') => void
}

const FiltersLayoutContext = createContext<FiltersLayoutContextType | undefined>(undefined)

interface FiltersLayoutProviderProps {
  children: ReactNode
}

export function FiltersLayoutProvider({ children }: FiltersLayoutProviderProps) {
  const {
    data: { pageFilters: _filters },
  } = useGetAppPage()

  // consider storing this state on local storage / session storage.
  // This will allow us to persist the filter layout state across page reloads.
  // This will be relevant if we want to allow users to save their layout configurations.
  const [pageFilters, setPageFilters] = useState(() => [..._filters])

  useEffect(() => {
    setPageFilters(_filters)
  }, [_filters])

  const unpinFilter = useCallback((selectedF: TPageFilter) => {
    setPageFilters((current) => {
      const newFilters = current.map((f) => (f.id === selectedF.id ? { ...f, pinned: undefined } : f))
      return newFilters
    })
  }, [])

  const pinFilter = useCallback((selectedF: TPageFilter, side: 'left' | 'right') => {
    setPageFilters((current) => {
      const newFilters = current.map((f) => (f.id === selectedF.id ? { ...f, pinned: side } : f))
      return newFilters
    })
  }, [])

  const pinnedFilters = useMemo(() => pageFilters.filter((filter) => filter.pinned), [pageFilters])
  const unPinnedFilters = useMemo(() => pageFilters.filter((filter) => !filter.pinned), [pageFilters])

  const value = {
    pageFilters,
    pinnedFilters,
    unPinnedFilters,
    unpinFilter,
    pinFilter,
  }

  return <FiltersLayoutContext.Provider value={value}>{children}</FiltersLayoutContext.Provider>
}

export function useFiltersLayout() {
  const context = useContext(FiltersLayoutContext)
  if (context === undefined) {
    throw new Error('useFiltersLayout must be used within a FiltersLayoutProvider')
  }
  return context
}
